import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Pencil,
  Trash,
} from "lucide-react";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import CommonDropdownMenu from "@/shared/components/ui/DropDown/DropdownMenu";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import Loader from "@/shared/components/ui/Loaders/Loader";
import NoteEditorDrawer from "../NoteEditorDrawer";
import {
  useCreateAccountDealNote,
  useDeleteAccountDealNote,
  useFetchAccountDealNotes,
  useUpdateAccountDealNote,
} from "../../hooks/useAccounts";

const touchPointLabels = {
  call: "Call",
  email: "Email",
  meeting: "Meeting",
  whatsapp: "WhatsApp",
  linkedin: "LinkedIn",
  other: "Other",
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "No date";

const Notes = ({ dealId }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState(null);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const { data, isLoading, isError } = useFetchAccountDealNotes({ dealId });
  const closeEditor = () => {
    setShowEditor(false);
    setNoteToEdit(null);
  };
  const { mutate: createNote, isPending: isCreating } =
    useCreateAccountDealNote({ dealId, action: closeEditor });
  const { mutate: updateNote, isPending: isUpdating } =
    useUpdateAccountDealNote({ dealId, action: closeEditor });
  const { mutate: deleteNote, isPending: isDeleting } =
    useDeleteAccountDealNote({
      dealId,
      action: () => setNoteToDelete(null),
    });

  const notes = data?.notes ?? [];
  const openEditor = (note = null) => {
    setNoteToEdit(note);
    setShowEditor(true);
  };

  const handleSave = (note) => {
    if (noteToEdit) {
      updateNote({ noteId: noteToEdit.id, note });
      return;
    }
    createNote(note);
  };

  const noteActions = (note) => [
    {
      id: "edit",
      name: "Edit",
      icon: <Pencil size={16} />,
      onClick: () => openEditor(note),
    },
    {
      id: "delete",
      name: "Delete",
      icon: <Trash size={16} />,
      onClick: () => setNoteToDelete(note),
    },
  ];

  return (
    <section className="mb-2 overflow-hidden rounded-[12px] border font-sans">
      <div className="flex items-center justify-between gap-3 p-4">
        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className="flex items-center gap-2 text-[14px] font-medium text-gray-800"
          aria-expanded={isOpen}
        >
          <FileText size={17} className="text-secondary" />
          Notes ({notes.length})
          {isOpen ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </button>
        <PrimaryButton onClick={() => openEditor()}>Add Note</PrimaryButton>
      </div>

      {isOpen && (
        <div className="border-t p-4">
          {isLoading ? (
            <div className="flex min-h-28 items-center justify-center">
              <Loader text="Loading notes..." />
            </div>
          ) : isError ? (
            <p className="py-8 text-center text-sm text-error-500">
              Unable to load notes.
            </p>
          ) : notes.length === 0 ? (
            <div className="flex min-h-28 flex-col items-center justify-center gap-2 text-center">
              <FileText size={28} className="text-gray-300" />
              <p className="text-sm font-medium text-gray-700">No notes yet</p>
              <p className="text-xs text-gray-500">
                Add the first interaction or deal update.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {notes.map((note) => (
                <article
                  key={note.id}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span className="rounded-full bg-[#207C971A] px-2 py-1 font-medium text-secondary">
                        {touchPointLabels[note.touch_point_type] || "Note"}
                      </span>
                      <span>
                        {formatDate(note.touch_point_date || note.created_at)}
                      </span>
                    </div>
                    <CommonDropdownMenu
                      actions={noteActions(note)}
                      itemId={note.id}
                      iconSize={16}
                    />
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-800">
                    {note.body}
                  </p>
                </article>
              ))}
            </div>
          )}
        </div>
      )}

      <NoteEditorDrawer
        isOpen={showEditor}
        onClose={closeEditor}
        noteToEdit={noteToEdit}
        isSaving={isCreating || isUpdating}
        onSave={handleSave}
      />

      <DeleteConfirmationModal
        showConfirmDelete={Boolean(noteToDelete)}
        onClose={() => setNoteToDelete(null)}
        title="Delete this note?"
        description="This note will be permanently removed from the deal."
        onDelete={() => deleteNote({ noteId: noteToDelete.id })}
        isDeleting={isDeleting}
        deleteButtonText="Delete Note"
      />
    </section>
  );
};

export default Notes;
